import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container, Row, Col, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";

import { useAuthUser } from "../../hooks/AuthProvider";
import { useRoleContext, ROLE_PM, ROLE_TENANT, ROLE_HOMES } from "../../context/RoleContext";

import "./landing.scss";

import porcherMark from "../../assets/images/porcher-logo.png";
import porcherWordmark from "../../assets/images/porcher-wordmark.png";
import heroArt from "../../assets/images/porcher-landing-image.png";
import screeningPhoto from "../../assets/images/happy-girl.png";
import { logAnalyticsEvent } from "../../firebaseConfig";

const journeySteps = [
  { icon: "bx bx-buildings", title: "List", desc: "Create a professional listing in minutes." },
  { icon: "bx bx-search-alt-2", title: "Screen", desc: "Review applicants and make an informed choice." },
  { icon: "bx bx-file", title: "Lease", desc: "Move from approved applicant to signed lease." },
  { icon: "bx bx-credit-card", title: "Collect", desc: "Get rent paid and tracked automatically." },
  { icon: "bx bx-cog", title: "Manage", desc: "Handle the day-to-day from one dashboard." },
];

const supportItems = [
  {
    icon: "bx bx-file-blank",
    title: "Help with rental documents",
    desc: "Guidance and templates for the paperwork that comes with renting out a home.",
  },
  {
    icon: "bx bx-compass",
    title: "Property management guidance",
    desc: "Straightforward answers on what to do next, at every stage of a tenancy.",
  },
  {
    icon: "bx bx-wrench",
    title: "Local handymen & service pros",
    desc: "Find trusted help for repairs and maintenance when you need an extra hand.",
    soon: true,
  },
  {
    icon: "bx bx-calendar-check",
    title: "Maintenance coordination",
    desc: "Keep repair requests, vendors, and follow-ups organized in one place.",
    soon: true,
  },
  {
    icon: "bx bx-clipboard",
    title: "Property inspections",
    desc: "Document the condition of your property at move-in, move-out, and beyond.",
  },
  {
    icon: "bx bx-support",
    title: "Support when you're stuck",
    desc: "Reach a real person when a situation calls for more than a checklist.",
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const user = useAuthUser();
  const roleCtx = useRoleContext();
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Porcher | Rent and Manage Your Property Yourself";
  }, []);

  const goToDashboard = (role) => {
    setDashboardOpen(false);
    logAnalyticsEvent("dashboard_menu_click", {
      dashboard_role: role === ROLE_PM ? "property_dashboard" : "tenant_dashboard",
    });
    if (user) {
      roleCtx?.setActiveRole(role);
      navigate(ROLE_HOMES[role]);
    } else {
      navigate(`/login?mode=${role}`);
    }
  };

  return (
    <div className="porcher-page">
      {/* Navbar */}
      <nav className="porcher-navbar py-3">
        <Container className="d-flex align-items-center justify-content-between">
          <Link className="porcher-brand" to="/">
            <img src={porcherMark} alt="" className="p-mark" />
            <img src={porcherWordmark} alt="Porcher" className="p-wordmark" />
          </Link>

          <div className="d-none d-lg-flex align-items-center">
            <a href="#how-it-works" className="porcher-nav-link" data-analytics-event="nav_how_it_works_click" data-analytics-area="landing_nav">How It Works</a>
            <Link to="/pricing" className="porcher-nav-link" data-analytics-event="nav_pricing_click" data-analytics-area="landing_nav">Pricing</Link>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Link to="/rentals" className="porcher-nav-link" data-analytics-event="nav_available_rentals_click" data-analytics-area="landing_nav">Available Rentals</Link>
            <Link to="/login" className="porcher-nav-link" data-analytics-event="nav_login_click" data-analytics-area="landing_nav">Sign In</Link>
            <Dropdown className="d-none d-lg-block" isOpen={dashboardOpen} toggle={() => setDashboardOpen((o) => !o)}>
              <DropdownToggle tag="a" caret className="porcher-nav-link" style={{ cursor: "pointer" }}>
                Dashboard
              </DropdownToggle>
              <DropdownMenu end className="porcher-dropdown-menu">
                <DropdownItem onClick={() => goToDashboard(ROLE_PM)}>
                  <div className="fw-semibold">Property Dashboard</div>
                  <div className="p-text-muted small">Manage properties, applications &amp; leases</div>
                </DropdownItem>
                <DropdownItem onClick={() => goToDashboard(ROLE_TENANT)}>
                  <div className="fw-semibold">Tenant Dashboard</div>
                  <div className="p-text-muted small">Pay rent, view lease &amp; requests</div>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <Link to="/register" className="p-btn p-btn-primary d-none d-lg-inline-flex" data-analytics-event="nav_register_click" data-analytics-area="landing_nav">List Your Property</Link>
            <Dropdown className="d-lg-none" isOpen={mobileMenuOpen} toggle={() => setMobileMenuOpen((open) => !open)}>
              <DropdownToggle
                className="porcher-mobile-menu-button"
                aria-label="Open navigation menu"
                title="Menu"
              >
                <i className="bx bx-menu" aria-hidden="true" />
              </DropdownToggle>
              <DropdownMenu end className="porcher-dropdown-menu">
                <DropdownItem tag={Link} to="/pricing" data-analytics-event="nav_pricing_click" data-analytics-area="mobile_nav">Pricing</DropdownItem>
                <DropdownItem tag="a" href="#how-it-works" data-analytics-event="nav_how_it_works_click" data-analytics-area="mobile_nav">How It Works</DropdownItem>
                <DropdownItem divider />
                <DropdownItem header>Dashboard</DropdownItem>
                <DropdownItem onClick={() => goToDashboard(ROLE_PM)}>
                  <div className="fw-semibold">Property Dashboard</div>
                  <div className="p-text-muted small">Manage properties, applications &amp; leases</div>
                </DropdownItem>
                <DropdownItem onClick={() => goToDashboard(ROLE_TENANT)}>
                  <div className="fw-semibold">Tenant Dashboard</div>
                  <div className="p-text-muted small">Pay rent, view lease &amp; requests</div>
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem tag={Link} to="/register" className="fw-semibold" data-analytics-event="nav_register_click" data-analytics-area="mobile_nav">List Your Property</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </Container>
      </nav>

      {/* Hero */}
      <section className="porcher-hero">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <span className="porcher-hero-tagline">
                List. Screen. Lease. Collect. <span className="p-text-purple">Manage.</span>
              </span>
              <h1>Rent your property. Manage it yourself.</h1>
              <p className="porcher-hero-lead">
                Porcher gives self-managed homeowners everything they need to list a rental,
                screen tenants, sign a lease, and collect rent — without hiring a property
                management company.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a href="#how-it-works" className="p-btn p-btn-primary p-btn-lg" data-analytics-event="hero_how_it_works_click" data-analytics-area="landing_hero">
                  See How It Works
                </a>
                {user ? (
                  <button type="button" className="p-btn p-btn-outline p-btn-lg" onClick={() => goToDashboard(ROLE_TENANT)}>
                    Pay Rent Now
                  </button>
                ) : (
                  <Link to="/rentals" className="p-btn p-btn-outline p-btn-lg" data-analytics-event="hero_available_rentals_click" data-analytics-area="landing_hero">
                    View Available Rentals
                  </Link>
                )}
              </div>
              <div className="porcher-hero-trust">
                <span><i className="bx bx-check-shield" /> No property manager required</span>
                <span><i className="bx bx-check-shield" /> Credit, criminal &amp; income checks built in</span>
                <span><i className="bx bx-check-shield" /> You stay in control</span>
              </div>
            </Col>
            <Col lg={6} className="porcher-hero-art">
              <img src={heroArt} alt="A Porcher rental listing, applicants, lease, and rent collection shown on desktop and mobile" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* How Porcher Works */}
      <section className="porcher-section" id="how-it-works">
        <Container>
          <span className="porcher-eyebrow">How Porcher Works</span>
          <h2 className="porcher-section-title">Everything you need, in five simple steps</h2>
          <p className="porcher-section-lead">
            Porcher walks you through the entire life of a tenancy — so you always know
            what to do next.
          </p>

          <div className="porcher-journey">
            {journeySteps.map((step, idx) => (
              <React.Fragment key={step.title}>
                <div className="porcher-journey-step">
                  <span className="p-step-num">{idx + 1}</span>
                  <div className="p-step-icon">
                    <i className={step.icon} />
                  </div>
                  <h5>{step.title}</h5>
                  <p>{step.desc}</p>
                </div>
                {idx < journeySteps.length - 1 && (
                  <div className="porcher-journey-arrow d-none d-lg-flex">
                    <i className="bx bx-chevron-right" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </Container>
      </section>

      {/* Tenant Screening Value Proposition */}
      <section className="porcher-section">
        <Container>
          <div className="porcher-value-band">
            <div className="porcher-value-inner">
              <Row className="align-items-center">
                <Col lg={6}>
                  <span className="porcher-eyebrow" style={{ color: "#c4b5fd" }}>Screen With Confidence</span>
                  <h2>Know who you're renting to before you hand over the keys</h2>
                  <p>
                    Porcher brings the information you need together in one place, so
                    you can compare applicants and make a confident decision.
                  </p>
                  <div className="porcher-value-points">
                    <div>
                      <i className="bx bx-credit-card" />
                      <h6>Credit insights at a glance</h6>
                      <p>Review credit scores and key credit details without digging through a complicated report.</p>
                    </div>
                    <div>
                      <i className="bx bx-check-shield" />
                      <h6>Go beyond the credit score</h6>
                      <p>See criminal and eviction screening results alongside the rest of the application.</p>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            <div
              className="porcher-value-photo"
              style={{ backgroundImage: `url(${screeningPhoto})` }}
              aria-hidden="true"
            />
          </div>
        </Container>
      </section>

      {/* Feature Sections */}
      <section className="porcher-section" id="features">
        <Container>
          <span className="porcher-eyebrow">The Porcher Platform</span>
          <h2 className="porcher-section-title">Built around how you actually rent a home</h2>
          <p className="porcher-section-lead">
            No dashboards full of features you'll never touch — just the tools that get a
            property listed, rented, and cared for.
          </p>

          {/* Listings */}
          <div className="porcher-feature">
            <div className="porcher-feature-copy">
              <div className="porcher-feature-icon"><i className="bx bx-buildings" /></div>
              <h3>A listing that looks professional, without the work</h3>
              <p>
                Add your property details and photos, and Porcher builds a clean, shareable
                listing you can send to prospective tenants right away. Use the link anywhere
                you already market your rental, then bring the application, screening, lease,
                and payment workflow back into Porcher.
              </p>
              <ul className="porcher-feature-list">
                <li><i className="bx bx-check" /> Guided listing builder for owners, not agents</li>
                <li><i className="bx bx-check" /> A professional listing URL for signs, email, social posts, and ads</li>
                <li><i className="bx bx-check" /> Renter inquiries and applications flow back to your dashboard</li>
                <li>
                  <i className="bx bx-check" /> Publish to sites like Homes.com
                  <span className="p-pill p-pill-soon ms-2">Coming Soon</span>
                </li>
              </ul>
            </div>
            <div className="porcher-feature-visual">
              <div className="porcher-feature-panel">
                <div className="p-panel-head">
                  <span className="p-panel-title"><i className="bx bxs-home-circle" /> 123 Maple Street</span>
                  <span className="p-pill">Published</span>
                </div>
                <div className="p-panel-row"><span>Bedrooms / Baths</span><strong>3 bd &middot; 2 ba</strong></div>
                <div className="p-panel-row"><span>Rent</span><strong>$2,200/mo</strong></div>
                <div className="p-panel-row"><span>Available</span><strong>Sept 1</strong></div>
              </div>
            </div>
          </div>

          {/* Applications & Screening */}
          <div className="porcher-feature porcher-feature--reverse porcher-feature--boxed">
            <div className="porcher-feature-copy">
              <div className="porcher-feature-icon"><i className="bx bx-search-alt-2" /></div>
              <h3>Find and screen tenants with confidence</h3>
              <p>
                Applicants apply online through a simple rental application — at no cost to
                you as the homeowner. Review everything in one place and make an informed
                decision, without needing to be an expert.
              </p>
              <ul className="porcher-feature-list">
                <li><i className="bx bx-check" /> Online rental applications, free for owners</li>
                <li>
                  <i className="bx bx-check" /> Credit reports &amp; scores
                  <span className="p-pill p-pill-soon ms-2">Coming Soon</span>
                </li>
                <li>
                  <i className="bx bx-check" /> Criminal &amp; eviction history checks
                  <span className="p-pill p-pill-soon ms-2">Coming Soon</span>
                </li>
                <li>
                  <i className="bx bx-check" /> Income verification
                  <span className="p-pill p-pill-soon ms-2">Coming Soon</span>
                </li>
              </ul>
            </div>
            <div className="porcher-feature-visual">
              <div className="porcher-feature-panel">
                <div className="p-panel-head">
                  <span className="p-panel-title"><i className="bx bx-user-check" /> Applicants</span>
                  <span className="p-pill">2 Received</span>
                </div>
                <div className="p-panel-row p-panel-row--applicant">
                  <div>
                    <strong>Sarah M.</strong>
                    <span className="p-panel-subtext">Screening pending</span>
                  </div>
                  <span className="p-pill">Applied</span>
                </div>
                <div className="p-panel-row p-panel-row--applicant">
                  <div>
                    <strong>James T.</strong>
                    <span className="p-panel-subtext">Screening pending</span>
                  </div>
                  <span className="p-pill">Approved</span>
                </div>
              </div>
            </div>
          </div>

          {/* Leasing */}
          <div className="porcher-feature">
            <div className="porcher-feature-copy">
              <div className="porcher-feature-icon"><i className="bx bx-file" /></div>
              <h3>From approved applicant to signed lease</h3>
              <p>
                Once you've chosen a tenant, Porcher helps you move forward — keeping leases,
                move-in details, and tenant records organized so nothing falls through the
                cracks.
              </p>
              <ul className="porcher-feature-list">
                <li><i className="bx bx-check" /> Lease and document storage for every tenancy</li>
                <li><i className="bx bx-check" /> Move-in and move-out workflows</li>
                <li><i className="bx bx-check" /> Tenant records &amp; property documentation, all in one place</li>
              </ul>
            </div>
            <div className="porcher-feature-visual">
              <div className="porcher-feature-panel">
                <div className="p-panel-head">
                  <span className="p-panel-title"><i className="bx bxs-key" /> Jordan K.</span>
                  <span className="p-pill">Signed</span>
                </div>
                <div className="p-panel-row"><span>Lease Term</span><strong>Jan 1 – Dec 31</strong></div>
                <div className="p-panel-row"><span>Move-in Date</span><strong>Jan 1</strong></div>
              </div>
            </div>
          </div>

          {/* Rent Collection */}
          <div className="porcher-feature porcher-feature--reverse porcher-feature--boxed">
            <div className="porcher-feature-copy">
              <div className="porcher-feature-icon"><i className="bx bx-credit-card" /></div>
              <h3>Get paid, without the chasing</h3>
              <p>
                Manage rent payments through Porcher and keep the entire rental relationship
                organized in one place — no more spreadsheets, reminders, or wondering who's
                paid.
              </p>
              <ul className="porcher-feature-list">
                <li><i className="bx bx-check" /> Rent collection tracked in one dashboard</li>
                <li><i className="bx bx-check" /> A clear history of every payment</li>
                <li><i className="bx bx-check" /> Everything about your rental, in one place</li>
              </ul>
            </div>
            <div className="porcher-feature-visual">
              <div className="porcher-feature-panel">
                <div className="p-panel-head">
                  <span className="p-panel-title"><i className="bx bx-wallet" /> August Rent</span>
                  <span className="p-pill">Paid</span>
                </div>
                <div className="p-panel-row"><span>Amount</span><strong>$2,200</strong></div>
                <div className="p-panel-row"><span>Paid On</span><strong>Aug 1</strong></div>
                <div className="p-panel-row"><span>Next Due</span><strong>Sep 1</strong></div>
              </div>
            </div>
          </div>

          {/* Inspections & Property Management */}
          <div className="porcher-feature">
            <div className="porcher-feature-copy">
              <div className="porcher-feature-icon"><i className="bx bx-clipboard" /></div>
              <h3>Keep your property in good hands</h3>
              <p>
                Document property conditions, schedule inspections, and keep a running record
                of your property over time — so you always know how it's being cared for.
              </p>
              <ul className="porcher-feature-list">
                <li><i className="bx bx-check" /> Move-in &amp; move-out inspections</li>
                <li><i className="bx bx-check" /> Ongoing property documentation</li>
                <li><i className="bx bx-check" /> A record you can look back on, anytime</li>
              </ul>
            </div>
            <div className="porcher-feature-visual">
              <div className="porcher-feature-panel">
                <div className="p-panel-head">
                  <span className="p-panel-title"><i className="bx bx-camera" /> Move-in Inspection</span>
                  <span className="p-pill">Complete</span>
                </div>
                <div className="p-panel-row"><span>Photos Uploaded</span><strong>14</strong></div>
                <div className="p-panel-row"><span>Completed</span><strong>Aug 5</strong></div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Get Help When You Need It */}
      <section className="porcher-section porcher-section-soft" id="support">
        <Container>
          <span className="porcher-eyebrow">Get Help When You Need It</span>
          <h2 className="porcher-section-title">Self-managed doesn't mean on your own</h2>
          <p className="porcher-section-lead">
            Porcher is here to make the harder parts easier — from paperwork to
            maintenance — so you never feel stuck.
          </p>

          <Row className="g-4 mt-2">
            {supportItems.map((item) => (
              <Col md={6} lg={4} key={item.title}>
                <div className="porcher-support-card">
                  <div className="p-icon"><i className={item.icon} /></div>
                  <h5>
                    {item.title}
                    {item.soon && <span className="p-pill p-pill-soon ms-2">Coming Soon</span>}
                  </h5>
                  <p>{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="porcher-section">
        <Container>
          <div className="porcher-cta-band">
            <h2>Ready to rent your property, your way?</h2>
            <p>Create your first listing on Porcher and stay in control from day one.</p>
            <div className="d-flex flex-wrap gap-3 justify-content-center">
              <Link to="/register" className="p-btn p-btn-primary p-btn-lg" data-analytics-event="cta_register_click" data-analytics-area="landing_bottom_cta">
                List Your Property
              </Link>
              <Link to="/login" className="p-btn p-btn-outline p-btn-lg" data-analytics-event="cta_login_click" data-analytics-area="landing_bottom_cta">
                Sign In
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <footer className="porcher-footer">
        <Container>
          <Row>
            <Col lg={4} className="mb-4 mb-lg-0">
              <img src={porcherWordmark} alt="Porcher" className="p-wordmark" />
              <p>Everything a self-managed homeowner needs to rent and manage a property.</p>
              <div className="porcher-footer-contact">
                <a href="mailto:contact@porcher.ai">contact@porcher.ai</a>
                <a href="tel:+17028991226">702-899-1226</a>
              </div>
            </Col>
            <Col lg={2} md={4} xs={6} className="mb-4 mb-lg-0">
              <h6>Product</h6>
              <ul>
                <li><a href="#how-it-works">How It Works</a></li>
                <li><a href="#features">Features</a></li>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><a href="#support">Support</a></li>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </Col>
            <Col lg={2} md={4} xs={6} className="mb-4 mb-lg-0">
              <h6>Account</h6>
              <ul>
                <li><Link to="/register">List Your Property</Link></li>
                <li><Link to="/login">Sign In</Link></li>
              </ul>
            </Col>
            <Col lg={2} md={4} xs={6}>
              <h6>Legal</h6>
              <ul>
                <li><Link to="/terms-of-service">Terms of Service</Link></li>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/tenant-screening-policy">Tenant Screening &amp; FCRA</Link></li>
                <li><Link to="/fair-housing-policy">Fair Housing</Link></li>
                <li><Link to="/community-guidelines">Community Guidelines</Link></li>
                <li><Link to="/payments-refund-policy">Payments &amp; Refunds</Link></li>
              </ul>
            </Col>
          </Row>
          <div className="porcher-footer-bottom">
            &copy; {new Date().getFullYear()} Porcher. All rights reserved.
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default LandingPage;
